import React, { useState } from 'react';
import {
  Box,
  Icon,
  type IconKind,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import SodCustomDonutChart from '../../../../shared/components/SodCustomDonutChart/CustomDonutChart';
import type {
  ExploitCategory,
  ReportSummary,
} from '../../data-layer/insightDashboardTypes';

import './StatCards.css';

const TOTAL_LABEL = 'Total';
const DOLLAR = '$';

interface StatCardItem {
  label: string;
  value: string | number;
  iconKind: string;
  accentVar: string;
}

interface StatCardsProps {
  summary: ReportSummary;
  exploitCategories?: ExploitCategory[];
  className?: string;
}

/**
 * Summary stat cards and exploit-category donut for the insight dashboard header.
 */
function StatCards({
  summary,
  exploitCategories = [],
  className,
}: StatCardsProps) {
  const [isExploitModalOpen, setIsExploitModalOpen] = useState(false);

  const exploitTotal = exploitCategories.reduce((sum, c) => sum + c.value, 0);

  const donutData = exploitCategories.map((c) => ({
    category: c.category,
    value: c.value,
  }));

  const donutColors = exploitCategories.map((c) => c.color);

  const stats: StatCardItem[] = [
    {
      label: 'SECURITY FINDINGS',
      value: summary.securityFindings,
      iconKind: 'ShieldOutline',
      accentVar: '--id-severity-critical',
    },
    {
      label: 'EXPLOITS',
      value: `$${exploitTotal.toLocaleString()}+`,
      iconKind: '',
      accentVar: '--id-severity-high',
    },
    {
      label: 'EVENTS ANALYZED',
      value: summary.totalEvents,
      iconKind: 'ActivityOutline',
      accentVar: '--id-severity-medium',
    },
    {
      label: 'T-CODE DEVIATIONS',
      value: summary.tCodeDeviations,
      iconKind: '',
      accentVar: '--id-severity-low',
    },
  ];

  return (
    <Box className={cx('Sod-StatCards', className)}>
      {stats.map((stat) => {
        const isExploits = stat.label === 'EXPLOITS';

        const cardClassName = cx('Sod-StatCards-card', {
          'Sod-StatCards-card--exploits': isExploits,
        });
        const cardStyle = { borderLeftColor: `var(${stat.accentVar})` };
        const cardContent = (
          <Box className='Sod-StatCards-card-content'>
            <Box
              className='Sod-StatCards-card-text'
              style={{ color: `var(${stat.accentVar})` }}>
              <Typography kind='label' className='Sod-StatCards-card-label'>
                {stat.label}
              </Typography>
              <Typography kind='h2' className='Sod-StatCards-card-value'>
                {stat.value}
              </Typography>
            </Box>

            {isExploits && donutData.length > 0 && (
              <div className='Sod-StatCards-card-donut'>
                <SodCustomDonutChart
                  data={donutData}
                  colorRange={donutColors}
                  value={exploitTotal}
                  label=''
                  width={56}
                  height={56}
                  innerRadius={20}
                  animate
                  animationDuration={800}
                  easing='easeOutQuad'
                />
              </div>
            )}

            {!isExploits && stat.iconKind && (
              <Box
                className='Sod-StatCards-card-icon'
                style={{ color: `var(${stat.accentVar})` }}>
                <Icon
                  kind={stat.iconKind as unknown as IconKind}
                  size='medium'
                />
              </Box>
            )}
          </Box>
        );

        if (isExploits) {
          return (
            <button
              key={stat.label}
              type='button'
              className={cardClassName}
              style={cardStyle}
              onClick={() => setIsExploitModalOpen(true)}>
              {cardContent}
            </button>
          );
        }

        return (
          <div key={stat.label} className={cardClassName} style={cardStyle}>
            {cardContent}
          </div>
        );
      })}

      {/* Exploits detail modal */}
      <ModalIsland
        isOpen={isExploitModalOpen}
        onClose={() => setIsExploitModalOpen(false)}
        title='Exploits Breakdown'
        contentBody={
          <div className='Sod-StatCards-exploitModal'>
            <div className='Sod-StatCards-exploitModal-chart'>
              <SodCustomDonutChart
                data={donutData}
                colorRange={donutColors}
                value={exploitTotal}
                label='Total'
                width={180}
                height={180}
                innerRadius={65}
                animate
                animationDuration={800}
                easing='easeOutQuad'
                hoverConfig={{
                  enableHalo: true,
                  haloWidth: 10,
                  haloOpacity: 0.3,
                }}
              />
            </div>
            <div className='Sod-StatCards-exploitModal-list'>
              {exploitCategories.map((cat) => (
                <div
                  key={cat.category}
                  className='Sod-StatCards-exploitModal-row'>
                  <span
                    className='Sod-StatCards-exploitModal-dot'
                    style={{ backgroundColor: cat.color }}
                  />
                  <span className='Sod-StatCards-exploitModal-name'>
                    {cat.category}
                  </span>
                  <span
                    className='Sod-StatCards-exploitModal-value'
                    style={{ color: cat.color }}>
                    {DOLLAR}
                    {cat.value.toLocaleString()}
                  </span>
                </div>
              ))}
              <div className='Sod-StatCards-exploitModal-total'>
                <span className='Sod-StatCards-exploitModal-totalLabel'>
                  {TOTAL_LABEL}
                </span>
                <span className='Sod-StatCards-exploitModal-totalValue'>
                  {DOLLAR}
                  {exploitTotal.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        }
      />
    </Box>
  );
}

StatCards.defaultProps = {
  exploitCategories: [],
  className: undefined,
};

export default StatCards;
